import { freeze } from 'icepick';
import flatMap from 'lodash/flatMap';
import flatten from 'lodash/flatten';
import keyBy from 'lodash/keyBy';
import trim from 'lodash/trim';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { getHierarchyLevelType } from '@atlassian/jira-issue-type-hierarchies';
import type {
	Issue,
	JiraPlanDataRestResponse,
	Sprint,
	Version as RestVersion,
} from '@atlassian/jira-software-backlog-uif-types/src/index.tsx';
import {
	BACKLOG,
	BOARD,
	KANBAN_BACKLOG_STRATEGY,
	SPRINT,
	SPRINT_BACKLOG_STRATEGY,
} from '../../../model/constants';
import { transformCard } from '../../../model/issue/utils';
import type { Card, CardId, CardIdAsObjectKey, CardList, CardType } from '../../../model/types';
import type { Version } from '../../../model/version/types';
import {
	type PlanDataResponse,
	type KanbanPlanDataRestResponse,
	type Column as RestColumn,
	isKanbanPlanDataResponse,
} from '../../../rest/fetch-plan-data/types';
import type { ConfigPayload, SetAppDataPayload } from '../../../state/actions/app';
import {
	nextGenBacklogPageLoad,
	PAGE_LOAD_MARK_TRANSFORM_CRITICAL_REST_START,
	PAGE_LOAD_MARK_TRANSFORM_CRITICAL_REST_END,
	uifBacklogNewUiPageLoad,
} from '../../utils/performance-analytics';

const NONE = 'None';

const boardList = {
	id: BOARD,
	type: BOARD,
	name: 'Board',
	issueListKey: null,
};

const backlogList = freeze({
	id: BACKLOG,
	type: BACKLOG,
	name: 'Backlog',
	issueListKey: null,
});

const transformCards = (issues: Issue[]): Record<CardIdAsObjectKey, Card> =>
	keyBy(issues.map(transformCard), 'id');

const isInKanbanContainer = (issue: Issue, column?: RestColumn): boolean =>
	!!column && column.statusIds.includes(issue.statusId);

const transformKanbanCardsOrder = ({
	issues,
	selectedForDevelopmentColumn: board,
	backlogColumn: backlog,
}: KanbanPlanDataRestResponse): { [key: string]: CardId[] } => {
	const cardIdByContainerType: Record<string, CardId[]> = {
		[BOARD]: [],
		[BACKLOG]: [],
	};
	issues.forEach((issue) => {
		if (isInKanbanContainer(issue, board)) {
			cardIdByContainerType[BOARD].push(issue.id);
		} else if (isInKanbanContainer(issue, backlog)) {
			cardIdByContainerType[BACKLOG].push(issue.id);
		}
	});
	return cardIdByContainerType;
};

const transformConfig = (response: JiraPlanDataRestResponse): ConfigPayload => {
	const isKanban = isKanbanPlanDataResponse(response);
	return {
		requestColumnMigration: false,
		rankCustomFieldId: response.rankCustomFieldId || -1,
		version: {
			// We want the versions feature to be always true for CMP
			isVersionsFeatureEnabled: true,
		},
		allFeatures: [],
		backlogStrategy: isKanban ? KANBAN_BACKLOG_STRATEGY : SPRINT_BACKLOG_STRATEGY,
		kanbanBoardColumnId: isKanban ? response?.selectedForDevelopmentColumn?.id : null,
		kanbanBacklogColumnId: isKanban ? response?.backlogColumn?.id : null,
		hierarchyNomenclature: response?.flexibleNomenclatureData?.levelOneName
			? {
					firstLevelName: response?.flexibleNomenclatureData?.levelOneName,
				}
			: null,
		permissions: fg('jsw-perf-backlog-status-field-ui-mutation')
			? {
					CAN_CREATE_ISSUES: response.canCreateIssue,
					CAN_EDIT_ISSUES: response.canCreateIssue,
					CAN_MANAGE_SPRINTS: response.canManageSprints,
					// The BE will always return `true` otherwise it will return a 401 error
					CAN_VIEW_BOARD: true,
				}
			: undefined,
	};
};

const isValidDateSprint = (value: string): boolean => trim(value) !== NONE;

const getSprintDate = (value: string): string => (isValidDateSprint(value) ? value : '');

const transformSprint = (
	{ id, name, goal, isoStartDate, isoEndDate, daysRemaining, state, canUpdateSprint }: Sprint,
	canManageSprints: boolean,
): CardList => ({
	id: String(id),
	name,
	type: SPRINT,
	issueListKey: null,
	sprintData: {
		goal: goal || '',
		startDate: getSprintDate(isoStartDate),
		endDate: getSprintDate(isoEndDate),
		daysRemaining,
		state,
		canUpdateSprint: ff('jsw-uif-fix-backlog-sprint-permission_0t956')
			? canUpdateSprint
			: canManageSprints && canUpdateSprint,
	},
});

const transformVersions = (response: JiraPlanDataRestResponse): Record<string, Version> =>
	keyBy(
		flatMap(
			response.versionData?.versionsPerProject,
			(versions: RestVersion[], projectId: string): Version[] =>
				versions.map((version) => ({
					id: version.id,
					name: version.name,
					projectId: Number(projectId),
				})),
		),
		(version: Version) => String(version.id),
	);

const toVersionIds = (versions: RestVersion[] | undefined): string[] =>
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	versions ? versions.map((version) => String(version.id)) : ([] as string[]);

const transformVersionsOrder = (response: JiraPlanDataRestResponse): string[] =>
	response?.versionData?.versionsPerProject
		? flatten(
				Object.keys(response.versionData.versionsPerProject).map((projectId) =>
					toVersionIds(response.versionData.versionsPerProject[projectId]),
				),
			)
		: [];

const transformFilteredCardIds = (
	issues: Issue[],
	activeQuickFilters: Array<string | number>,
): Set<string> | null => {
	if (!activeQuickFilters.length) {
		return null;
	}
	return new Set(issues.map((issue) => String(issue.id)));
};

const transformIssueIdsBySprint = (issues: Issue[], sprintId: number) =>
	issues.filter((issue) => issue.sprintIds?.includes(sprintId) ?? false).map((issue) => issue.id);

const extractCardTypes = (issues: Issue[]) =>
	Array.from(
		issues
			.reduce((acc, val) => {
				if (acc.has(val.typeId)) return acc;
				return acc.set(val.typeId, {
					id: Number(val.typeId),
					name: val.typeName,
					iconUrl: val.typeUrl,
					hasRequiredFields: null,
					typeHierarchyLevel: getHierarchyLevelType(val.typeHierarchyLevel),
				});
			}, new Map<string, CardType>())
			.values(),
	);

const isSubtask = (issue: Issue): boolean =>
	typeof issue.parentKey === 'string' && issue.parentKey.length > 0;

// This is necessary as issues in the store may not be ordered with subtasks immediately after their parent.
export const orderBacklogIssueIds = (issues: Issue[], issueIdInSprints: number[]): number[] => {
	if (issues.length === 0) return [];

	const backlogIssues = issues.filter((issue) => !issueIdInSprints.includes(issue.id));
	const backlogIssueIds: Set<number> = new Set();

	if (backlogIssues.length > 0) {
		const subtasks = backlogIssues.filter(isSubtask);

		backlogIssues.forEach((issue) => {
			// In case the issue has already been processed return to prevent the loop below which is expensive.
			if (backlogIssueIds.has(issue.id)) return;

			backlogIssueIds.add(issue.id);

			// subtasks can not have subtasks and thus the work below is unnecessary.
			if (isSubtask(issue)) return;

			subtasks.forEach((subtask) => {
				if (subtask.parentId !== issue.id) return;

				backlogIssueIds.add(subtask.id);
			});
		});
	}

	return Array.from(backlogIssueIds);
};

const transformScrumBacklog = (
	response: JiraPlanDataRestResponse,
	activeQuickFilters: Array<string | number>,
): SetAppDataPayload => {
	const cardList: Record<string, CardList> = { [BACKLOG]: backlogList };
	const cardOrder: Record<string, CardId[]> = {};
	const cardListOrder: string[] = [];
	let issueIdInSprints: number[] = [];
	const visibleIssues = response.issues.filter((issue) => !issue.hidden);

	response.sprints.forEach((sprint) => {
		const sprintIssueIds = transformIssueIdsBySprint(response.issues, sprint.id);
		cardList[sprint.id] = transformSprint(sprint, response.canManageSprints);
		cardOrder[sprint.id] = sprintIssueIds;
		cardListOrder.push(String(sprint.id));
		issueIdInSprints = issueIdInSprints.concat(sprintIssueIds);
	});

	cardOrder[BACKLOG] = orderBacklogIssueIds(response.issues, issueIdInSprints);
	cardListOrder.push(BACKLOG);

	return {
		entities: {
			card: transformCards(response.issues),
			cardOrder,
			cardListOrder,
			cardList,
			cardTypes: extractCardTypes(response.issues),
			project: {
				projects: response.projects,
			},
			versions: transformVersions(response),
			version: {
				versions: transformVersionsOrder(response),
			},
		},
		config: transformConfig(response),
		ui: {
			cards: {
				filteredCards: {
					isLoading: false,
					filteredCardsIds: transformFilteredCardIds(visibleIssues, activeQuickFilters),
				},
			},
		},
	};
};

const transformBoardListWithLimits = (firstColumn: RestColumn): CardList => {
	if (firstColumn) {
		return {
			...boardList,
			name: firstColumn.name,
			minColumnLimit: firstColumn.min,
			maxColumnLimit: firstColumn.max,
		};
	}
	return boardList;
};

const transformKanbanBacklog = (
	response: KanbanPlanDataRestResponse,
	activeQuickFilters: Array<string | number>,
): SetAppDataPayload => {
	const visibleIssues = response.issues.filter((issue) => !issue.hidden);

	return {
		entities: {
			card: transformCards(response.issues),
			cardOrder: transformKanbanCardsOrder(response),
			cardOrderGlobal: response.issues.map((issue) => issue.id),
			cardListOrder: [BOARD, BACKLOG],
			cardList: {
				[BOARD]: transformBoardListWithLimits(response.selectedForDevelopmentColumn),
				[BACKLOG]: backlogList,
			},
			project: {
				projects: response.projects,
			},
			versions: transformVersions(response),
			version: {
				versions: transformVersionsOrder(response),
			},
		},
		config: transformConfig(response),
		ui: {
			cards: {
				filteredCards: {
					isLoading: false,
					filteredCardsIds: transformFilteredCardIds(visibleIssues, activeQuickFilters),
				},
			},
		},
	};
};

export const transformPlanData = (
	response: PlanDataResponse,
	isCompanyManaged: boolean,
	activeQuickFilters: Array<string | number> = [],
): SetAppDataPayload => {
	const metric = isCompanyManaged ? uifBacklogNewUiPageLoad : nextGenBacklogPageLoad;
	metric.mark(PAGE_LOAD_MARK_TRANSFORM_CRITICAL_REST_START);
	const result = isKanbanPlanDataResponse(response)
		? transformKanbanBacklog(response, activeQuickFilters)
		: transformScrumBacklog(response, activeQuickFilters);
	metric.mark(PAGE_LOAD_MARK_TRANSFORM_CRITICAL_REST_END);
	return result;
};
